import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import {
  Users,
  Team,
  CreateUserRequestInput,
  TicketInput,
  AccessRequestInput,
} from "../models/API";
import {
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { generateClient } from "aws-amplify/api";
import { createUserRequest, processTicketyEvents } from "../graphql/mutations";

interface RequestAccessDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (item: Users) => void;
  currentUserId: number;
  initialData: Team[];
  currentUserName: string;
}

const RequestAccessDialog: React.FC<RequestAccessDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  currentUserId,
  initialData,
  currentUserName,
}) => {
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
  const [teams, setTeams] = useState<Team[] | null>([]);
  const client = generateClient();

  useEffect(() => {
    setTeams(initialData);
  }, [initialData]);

  useEffect(() => {
    // Reset selection when dialog opens
    if (isOpen) {
      setSelectedTeamId(null);
    }
  }, [isOpen]);

  if (!isOpen || !teams) {
    return null;
  }

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedTeamId(Number(event.target.value));
  };

  function getTeamName(team_id: number): string {
    const team = teams!.find((item) => item.id === team_id);
    return team!.name;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    if (selectedTeamId === null) {
      alert("Please select a team to request access.");
      return;
    }

    try {
      console.log("team_id=" + selectedTeamId + ", user_id = " + currentUserId);
      const input: CreateUserRequestInput = {
        user_id: currentUserId,
        team_id: selectedTeamId,
      };
      await client.graphql({
        query: createUserRequest,
        variables: {
          input: input,
        },
      });
      const accessRequest: AccessRequestInput = {
        appPrefix: "FINN",
        ticketOwner: "FN",
        severity: "SEV_3",
        userName: currentUserName,
        teamName: getTeamName(selectedTeamId),
        reason: "New user onboarding",
      };
      const simInput: TicketInput = {
        accessRequest: accessRequest,
      };
      console.log("simInput=" + JSON.stringify(simInput));
      await client.graphql({
        query: processTicketyEvents,
        variables: {
          input: simInput,
        },
      });
      handleClose();
    } catch (err) {
      console.error(err);
      alert(
        "An error occurred while submitting your request. Please try again."
      );
    }
  };

  const handleClose = () => {
    setSelectedTeamId(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Request Access</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <FormLabel component="legend">
                Select a team to request access:
              </FormLabel>
              <RadioGroup
                aria-label="team"
                name="team-radio-group"
                value={selectedTeamId?.toString() || ""}
                onChange={handleRadioChange}
              >
                {teams.map((team: Team) => (
                  <FormControlLabel
                    key={team.id}
                    value={team.id.toString()}
                    control={<Radio />}
                    label={team.name}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          disabled={selectedTeamId === null}
          variant="contained"
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequestAccessDialog;
